"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";

import { cn } from "@/src/lib/utils";

// Two upright seals side by side in one line.
const stamps = [
  {
    src: "/icons/ISO 9001 black.svg",
    alt: "ISO 9001 certified",
    tilt: 0,
    offset: "",
  },
  {
    src: "/icons/ISO 27001 black.svg",
    alt: "ISO 27001 certified",
    tilt: 0,
    offset: "",
  },
];

// Bare seals, no container. The first time the row scrolls into view each seal
// is "pressed" onto the page (see isoStampIn in animations.css), staggered.
// Under prefers-reduced-motion they simply appear at their resting tilt.
export function IsoStamps({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn("flex items-center gap-4 sm:gap-4", className)}
    >
      {stamps.map((stamp, index) => (
        <div
          key={stamp.src}
          className={cn(
            "opacity-0 motion-reduce:opacity-90",
            stamp.offset,
            shown &&
              "motion-safe:animate-[isoStampIn_700ms_cubic-bezier(0.2,0.9,0.3,1.2)_both]",
          )}
          style={{
            ["--stamp-tilt" as string]: `${stamp.tilt}deg`,
            animationDelay: `${index * 250}ms`,
            transform: `rotate(${stamp.tilt}deg)`,
          }}
        >
          <Image
            src={stamp.src}
            alt={stamp.alt}
            width={223}
            height={223}
            className="size-32 sm:size-40"
          />
        </div>
      ))}
    </div>
  );
}
